import { Download, ArrowLeft, Printer } from 'lucide-react'
import { useLanguage } from '../context/LanguageContext'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import './ReportPage.css'

export default function ReportPage({ results, onBack }) {
  const { t } = useLanguage()

  if (!results) {
    return (
      <div className="report-container">
        <p>{t('report.title')} no disponible</p>
      </div>
    )
  }

  const data = [
    { name: t('dashboard.demand'), value: results.components.demand },
    { name: t('dashboard.margin'), value: results.components.margin },
    { name: t('dashboard.trend'), value: results.components.trend },
    { name: t('dashboard.audience'), value: results.components.audience },
    { name: t('dashboard.competition'), value: results.components.competition }
  ]

  const handlePrintPDF = () => {
    const printWindow = window.open('', '', 'height=600,width=800')
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${t('report.title')}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              line-height: 1.6;
              color: #333;
              margin: 0;
              padding: 20px;
              background: white;
            }
            .report-header {
              text-align: center;
              border-bottom: 3px solid #00e5ff;
              padding-bottom: 20px;
              margin-bottom: 30px;
            }
            .report-header h1 {
              color: #00e5ff;
              margin: 0;
              font-size: 28px;
            }
            .report-date {
              color: #666;
              font-size: 12px;
            }
            .score-section {
              background: #f5f5f5;
              padding: 20px;
              border-radius: 8px;
              text-align: center;
              margin: 20px 0;
            }
            .large-score {
              font-size: 48px;
              font-weight: bold;
              color: ${results.score >= 80 ? '#39ff14' : results.score >= 60 ? '#00e5ff' : '#ff6b6b'};
            }
            .recommendation {
              background: ${results.score >= 80 ? '#d4edda' : results.score >= 60 ? '#d1ecf1' : '#f8d7da'};
              color: ${results.score >= 80 ? '#155724' : results.score >= 60 ? '#0c5460' : '#721c24'};
              padding: 15px;
              border-radius: 4px;
              border-left: 4px solid ${results.score >= 80 ? '#28a745' : results.score >= 60 ? '#17a2b8' : '#dc3545'};
              margin: 20px 0;
            }
            .components-table {
              width: 100%;
              border-collapse: collapse;
              margin: 20px 0;
            }
            .components-table th,
            .components-table td {
              padding: 12px;
              text-align: left;
              border-bottom: 1px solid #ddd;
            }
            .components-table th {
              background: #00e5ff;
              color: white;
              font-weight: bold;
            }
            .components-table tr:nth-child(even) {
              background: #f9f9f9;
            }
            .footer {
              margin-top: 40px;
              padding-top: 20px;
              border-top: 1px solid #ddd;
              text-align: center;
              color: #666;
              font-size: 12px;
            }
            @media print {
              body { margin: 0; padding: 10mm; }
            }
          </style>
        </head>
        <body>
          <div class="report-header">
            <h1>${t('report.title')}</h1>
            <p>${t('report.analysisOf')}: <strong>${results.query}</strong></p>
            <p class="report-date">${t('report.generatedOn')}: ${new Date().toLocaleDateString()}</p>
          </div>

          <div class="score-section">
            <h2>${t('report.viabilityAssessment')}</h2>
            <div class="large-score">${results.score}/100</div>
            <p style="margin-top: 10px;">${results.status}</p>
          </div>

          <div class="recommendation">
            <strong>${t('report.recommendation')}:</strong><br/>
            ${results.recommendation}
          </div>

          <h3>${t('report.details')}</h3>
          <table class="components-table">
            <thead>
              <tr>
                <th>${t('report.details')}</th>
                <th>${t('report.confidence')}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>${t('dashboard.demand')}</td>
                <td>${results.components.demand}%</td>
              </tr>
              <tr>
                <td>${t('dashboard.margin')}</td>
                <td>${results.components.margin}%</td>
              </tr>
              <tr>
                <td>${t('dashboard.trend')}</td>
                <td>${results.components.trend}%</td>
              </tr>
              <tr>
                <td>${t('dashboard.audience')}</td>
                <td>${results.components.audience}%</td>
              </tr>
              <tr>
                <td>${t('dashboard.competition')}</td>
                <td>${results.components.competition}%</td>
              </tr>
            </tbody>
          </table>

          <div class="footer">
            <p>Market Research Intelligence Platform | ${new Date().getFullYear()}</p>
            <p>Este reporte fue generado automáticamente. Para más detalles, contacte soporte.</p>
          </div>
        </body>
      </html>
    `)
    printWindow.document.close()
    printWindow.print()
  }

  return (
    <div className="report-page">
      {/* Header del reporte */}
      <div className="report-header">
        <button className="btn-back" onClick={onBack}>
          <ArrowLeft width={18} height={18} />
          {t('report.close')}
        </button>

        <div className="report-title-section">
          <h1>{t('report.title')}</h1>
          <p className="report-subtitle">
            {t('report.analysisOf')}: <strong>{results.query}</strong>
          </p>
          <p className="report-date">
            {t('report.generatedOn')}: {new Date().toLocaleDateString()}
          </p>
        </div>

        <div className="report-actions">
          <button className="btn-print" onClick={handlePrintPDF}>
            <Printer width={18} height={18} />
            {t('report.downloadPDF')}
          </button>
          <button className="btn-download">
            <Download width={18} height={18} />
            JSON
          </button>
        </div>
      </div>

      {/* Contenido del reporte */}
      <div className="report-content">
        {/* Score principal */}
        <div className="score-card">
          <h2>{t('report.viabilityAssessment')}</h2>
          <div className="score-display">
            <div className={`score-circle score-${results.score >= 80 ? 'high' : results.score >= 60 ? 'medium' : 'low'}`}>
              {results.score}
            </div>
            <div className="score-info">
              <p className="score-status">{results.status}</p>
              <p className="score-label">/100</p>
            </div>
          </div>
        </div>

        {/* Recomendación */}
        <div className={`recommendation-card recommendation-${results.score >= 80 ? 'viable' : results.score >= 60 ? 'conditional' : 'not-viable'}`}>
          <h3>{t('report.recommendation')}</h3>
          <p>{results.recommendation}</p>
        </div>

        {/* Gráfico de componentes */}
        <div className="components-card">
          <h3>{t('report.marketAnalysis')}</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0, 229, 255, 0.1)" />
              <XAxis dataKey="name" tick={{ fill: '#a0a0a0', fontSize: 12 }} />
              <YAxis tick={{ fill: '#a0a0a0', fontSize: 12 }} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#1a1f3a', 
                  border: '1px solid #00e5ff',
                  borderRadius: '8px'
                }}
              />
              <Bar dataKey="value" fill="#00e5ff" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Tabla de detalles */}
        <div className="details-table">
          <h3>{t('report.details')}</h3>
          <table>
            <thead>
              <tr>
                <th>Componente</th>
                <th>{t('report.confidence')}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{t('dashboard.demand')}</td>
                <td className="value">{results.components.demand}%</td>
              </tr>
              <tr>
                <td>{t('dashboard.margin')}</td>
                <td className="value">{results.components.margin}%</td>
              </tr>
              <tr>
                <td>{t('dashboard.trend')}</td>
                <td className="value">{results.components.trend}%</td>
              </tr>
              <tr>
                <td>{t('dashboard.audience')}</td>
                <td className="value">{results.components.audience}%</td>
              </tr>
              <tr>
                <td>{t('dashboard.competition')}</td>
                <td className="value">{results.components.competition}%</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Footer del reporte */}
        <div className="report-footer">
          <p>Market Research Intelligence Platform © {new Date().getFullYear()}</p>
          <p className="footer-note">Este reporte fue generado automáticamente. Los datos están basados en análisis de mercado en tiempo real.</p>
        </div>
      </div>
    </div>
  )
}
